import { action, computed, observable, runInAction, when } from 'mobx'
import { isEqual } from 'lodash-es'
import { concatMap, debounceTime, filter, Subject, switchMap, take } from 'rxjs'
import type { Member } from '@/features/iam/members/Member'
import { LegendConflictError } from '@/features/legends/legend-conflict-error'
import type {
  LegendStore,
  SavedLegendVersion,
} from '@/features/legends/LegendStore'
import {
  emptyLegendDocument,
  type LegendDocument,
} from '@/features/legends/editor/legend-document'
import type { AlertErrorHandlerFactory } from '@/features/shell'
import type { ActivationLifecycle } from '@/infra/activation'
import type { Id } from '@/infra/id'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { tapAction, toStream } from '@/utils/mobx-util'

/**
 * How long to wait after a change before saving it.
 */
const PERSIST_WAIT = 1000

/**
 * The legend version that changes are saved to.
 */
export interface LegendDraftTarget {
  legendId: Id
  workspaceId: Id
  version: number
  /**
   * From the version's fetch or from the last save, never from a listing:
   * someone else's save since then must raise a conflict.
   */
  concurrencyVersion: number
  /**
   * Changing a published version saves a new draft after it.
   */
  published: boolean
}

/**
 * Dependencies of {@link LegendDraftPersister}.
 */
export interface LegendDraftPersisterOptions {
  legendStore: LegendStore
  /**
   * Whether the legend is being replaced, by a publish, revert or import.
   * Autosaves wait until it's done.
   */
  isBusy: () => boolean
  /**
   * Asks whether to save over someone else's changes, calling `force` to do
   * so. Resolves whether they were forced.
   *
   * `force` saves the latest document given for the version, which may be
   * newer than the one that conflicted.
   */
  resolveConflict: (
    lastUpdatedBy: Member,
    force: () => Promise<unknown>,
  ) => Promise<boolean>
  /**
   * Called when a change to the published version saved a new draft, while
   * that version is still the one being edited.
   */
  onDraftCreated: (version: number) => void
  createAlertErrorHandler: AlertErrorHandlerFactory
  /**
   * How long to wait after a change before saving it, in milliseconds.
   */
  wait?: number
}

/**
 * Options for {@link LegendDraftPersister.save}.
 */
export interface SaveLegendDraftOptions {
  /**
   * Sends the whole document, even when unchanged, instead of the changes.
   */
  full: boolean
  /**
   * Saved with the version, for the publish that follows.
   */
  versionNotes?: string
}

type SaveMode = 'Autosave' | 'Changes' | 'Full'

interface SaveRequest {
  session: DraftSession
  document: LegendDocument
  mode: SaveMode
  versionNotes?: string
  resolve: (saved: SavedLegendVersion | null) => void
  reject: (err: unknown) => void
}

/**
 * The editing of one load of a version: what was last saved of it, and where
 * it saves to. Loading a version starts a new session, which ends the saves
 * still waiting in the old one.
 */
class DraftSession {
  @observable.ref accessor target: LegendDraftTarget | null
  @observable.ref accessor lastSerialized: LegendDocument
  isEnded = false

  /**
   * The document last scheduled or saved. Forcing over a conflict saves this
   * one: changes made while the conflicting save ran wait behind it, and the
   * reload that follows drops them.
   */
  latest: LegendDocument

  constructor(target: LegendDraftTarget | null, baseline: LegendDocument) {
    this.target = target
    this.lastSerialized = baseline
    this.latest = baseline
  }
}

/**
 * Saves the legend editor's changes: a change once the user stops changing
 * the legend for a moment, and explicit saves right away. Saves run one at a
 * time, so that each diff builds on the previous save.
 */
export class LegendDraftPersister implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()
  private readonly _changes = new Subject<void>()
  private readonly _requests = new Subject<SaveRequest>()

  @observable.ref private accessor _session = new DraftSession(
    null,
    emptyLegendDocument,
  )
  @observable.ref private accessor _scheduled: {
    session: DraftSession
    document: LegendDocument
  } | null = null

  @observable private accessor _queued = 0

  /**
   * How long the last save took, in milliseconds.
   */
  @observable accessor lastSaveLatency = 0

  constructor(private readonly _options: LegendDraftPersisterOptions) {
    // Queues the scheduled change once changes pause and nothing replaces the
    // legend.
    this._lifecycle.manage(
      this._changes.pipe(
        debounceTime(_options.wait ?? PERSIST_WAIT),
        switchMap(() =>
          toStream(_options.isBusy, true).pipe(
            filter((isBusy) => !isBusy),
            take(1),
          ),
        ),
        tapAction(() => this._enqueueScheduled()),
      ),
    )
    // Not managed by the lifecycle: the saves queued when the editor closes
    // still have to run.
    this._requests
      .pipe(
        concatMap((request) =>
          this._run(request)
            .then(request.resolve, request.reject)
            .finally(action(() => this._queued--)),
        ),
      )
      .subscribe()
  }

  /**
   * The version changes are saved to; `null` when the loaded version is not
   * editable.
   */
  @computed
  get target() {
    return this._session.target
  }

  /**
   * The document as last saved, or as loaded.
   */
  @computed
  get lastSerialized() {
    return this._session.lastSerialized
  }

  /**
   * Whether a change waits to be saved, or a save is running.
   */
  @computed
  get isSaving() {
    return !!this._scheduled || this._queued > 0
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    // Saves a change made right before leaving instead of dropping it.
    void this.flush()
    this._lifecycle.deactivate()
  }

  /**
   * Starts editing a loaded version. Changes not saved yet were made to the
   * builders this replaces, and are dropped.
   *
   * @param target Where changes are saved; `null` to not save them.
   * @param baseline The document as loaded.
   */
  @action
  start(target: LegendDraftTarget | null, baseline: LegendDocument) {
    this._session.isEnded = true
    this._scheduled = null
    this._session = new DraftSession(target, baseline)
  }

  /**
   * Saves the document once changes pause. A later change replaces it.
   *
   * @param document
   */
  @action
  schedule(document: LegendDocument) {
    if (!this._session.target) {
      return
    }
    this._session.latest = document
    this._scheduled = { session: this._session, document }
    this._changes.next()
  }

  /**
   * Saves the document after the saves already queued, instead of the
   * scheduled change.
   *
   * @param document
   * @param options
   * @returns The outcome; `null` when nothing was saved, such as when another
   * version loaded first, or someone else's changes were kept.
   */
  @action
  save(
    document: LegendDocument,
    { full, versionNotes }: SaveLegendDraftOptions,
  ) {
    this._scheduled = null
    this._session.latest = document
    return this._enqueue(
      this._session,
      document,
      full ? 'Full' : 'Changes',
      versionNotes,
    )
  }

  /**
   * Saves the scheduled change now.
   *
   * @returns Resolves once every queued save ran.
   */
  async flush() {
    this._enqueueScheduled()
    await when(() => this._queued === 0)
  }

  @action
  private _enqueueScheduled() {
    const scheduled = this._scheduled
    this._scheduled = null
    if (scheduled) {
      // The alert reports a failure.
      this._enqueue(scheduled.session, scheduled.document, 'Autosave').catch(
        () => undefined,
      )
    }
  }

  @action
  private _enqueue(
    session: DraftSession,
    document: LegendDocument,
    mode: SaveMode,
    versionNotes?: string,
  ) {
    this._queued++
    return new Promise<SavedLegendVersion | null>((resolve, reject) =>
      this._requests.next({
        session,
        document,
        mode,
        versionNotes,
        resolve,
        reject,
      }),
    )
  }

  private async _run({
    session,
    document,
    mode,
    versionNotes,
  }: SaveRequest): Promise<SavedLegendVersion | null> {
    const lastPersisted = session.lastSerialized
    const isUnchanged = isEqual(lastPersisted, document)
    if (
      session.isEnded ||
      !session.target ||
      (mode !== 'Full' && isUnchanged)
    ) {
      return null
    }

    // An explicit save may not change anything; keeping the reference keeps
    // Discard changes hidden.
    const persisting = isUnchanged ? lastPersisted : document
    runInAction(() => {
      session.lastSerialized = persisting
    })

    const started = Date.now()
    try {
      return await this._send(
        session,
        document,
        mode === 'Full' ? null : lastPersisted,
        versionNotes,
      )
    } catch (err) {
      // The next save sends the change again.
      runInAction(() => {
        if (session.lastSerialized === persisting) {
          session.lastSerialized = lastPersisted
        }
      })
      if (err instanceof LegendConflictError) {
        let forced: SavedLegendVersion | null = null
        await this._options.resolveConflict(err.lastUpdatedBy, async () => {
          const latest = session.latest
          forced = await this._send(
            session,
            latest,
            null,
            versionNotes,
            true,
          ).catch(
            this._options.createAlertErrorHandler('Saving the legend failed'),
          )
          runInAction(() => {
            session.lastSerialized = latest
          })
        })
        return forced
      }
      return this._options.createAlertErrorHandler('Saving the legend failed')(
        err,
      )
    } finally {
      runInAction(() => {
        this.lastSaveLatency = Date.now() - started
      })
    }
  }

  /**
   * Sends the document to the session's version, or creates a draft after it
   * when it's published.
   *
   * @param session
   * @param document
   * @param previous What the server has; only the changes from it are sent.
   * `null` sends the whole document.
   * @param versionNotes Saved with the version.
   * @param force Saves over someone else's changes.
   */
  private async _send(
    session: DraftSession,
    document: LegendDocument,
    previous: LegendDocument | null,
    versionNotes?: string,
    force?: boolean,
  ) {
    // SAFETY: `_run` only sends for sessions with a target, which never unset.
    const target = session.target!
    const store = this._options.legendStore
    const params = {
      legendId: target.legendId,
      workspaceId: target.workspaceId,
      concurrencyVersion: target.concurrencyVersion,
      document,
      versionNotes,
      force,
    }

    if (target.published) {
      const version = target.version + 1
      const saved = await store.saveLegendVersion({ ...params, version })
      runInAction(() => {
        session.target = {
          ...target,
          version,
          concurrencyVersion: saved.concurrencyVersion,
          published: false,
        }
        session.lastSerialized = document
      })
      if (!session.isEnded) {
        this._options.onDraftCreated(version)
      }
      return saved
    }

    const saved = await (previous
      ? store.saveLegendVersionChanges({
          ...params,
          version: target.version,
          previous,
        })
      : store.saveLegendVersion({ ...params, version: target.version }))
    runInAction(() => {
      session.target = {
        ...target,
        concurrencyVersion: saved.concurrencyVersion,
      }
    })
    return saved
  }
}
